import { Component, ComponentInterface, h, Host, Prop, State, Watch } from "@stencil/core";
import Prism, { Grammar } from "prismjs";
import { debounce } from "../../utils/debounce";
import copyToClipboard from "../../utils/copyToClipboard";
import { logger } from "../../utils/console";

/** @static base class name */
const baseClass = "ux-code";

@Component({
    tag: "ux-code",
    styleUrl: "code.scss",
    shadow: false
})
export class Code implements ComponentInterface {
    private codeElement!: HTMLElement;

    /** The source code to display  */
    @Prop() readonly code: string = "";
    /** Label for the copy-to-clipboard button */
    @Prop() readonly copyButton: string = "Copy";
    /** The language used for syntax highlighting */
    @Prop() readonly language: string = "none";
    /** Flag that can be set to display line numbers */
    @Prop() readonly lineNumbers: boolean = false;
    /** display the code snippet inline instead of as a block */
    @Prop() readonly inline: boolean = false;
    /** Theme that should be used */
    @Prop() readonly theme: "light" | "dark" = "light";

    /** The formatted source code */
    @State() formattedCode: string;
    /** Indicates that the code has successfully been copied to clipboard */
    @State() isCopied: boolean;
    /** The calculated number of rows */
    @State() numLines = 0;

    @Watch("language")
    @Watch("code")
    updateCode() {
        this.formatCode();
    }

    componentWillLoad() {
        this.updateCode();
    }

    private formatCode = debounce(async () => {
        const config = {
            code: this.code.trim(),
            language: this.language === undefined ? "none" : this.language.toLowerCase(),
            grammar: this.language === undefined ? "none" : this.language.toLowerCase()
        };
        try {
            switch (config.language) {
                case "css":
                case "scss":
                case "sass":
                    config.grammar = Prism.languages.css as string;
                    break;
                case "js":
                case "javascript":
                case "typescript":
                case "react":
                case "jsx":
                case "tsx":
                case "php":
                    config.grammar = Prism.languages.javascript as string;
                    break;
                case "html":
                case "markup":
                case "xml":
                default:
                    config.grammar = Prism.languages.html as string;
                    break;
            }
            this.formattedCode = Prism.highlight(config.code, config.grammar as Grammar, config.language);
            this.codeElement.innerHTML = this.formattedCode;
            this.numLines = this.formattedCode.split("\n").length;
        } catch (err) {
            this.codeElement.innerHTML = config.code;
            this.numLines = config.code.split("\n").length;
            logger.error(`Failed to format code:`, err);
        }
    }, 100);

    private copySource = async () => {
        await copyToClipboard(this.code);
        this.isCopied = true;
        window.setTimeout(() => {
            this.isCopied = false;
        }, 2000);
    };

    private renderLineNumbers = (): HTMLDivElement => {
        const rows = Array.from(Array(this.numLines).keys());
        return (
            <div class={`${baseClass}__rows`}>
                {rows.map((row) => (
                    <span key={row}>{row + 1}</span>
                ))}
            </div>
        );
    };

    render() {
        const { language, theme, copyButton, copySource, isCopied, lineNumbers, numLines, inline } = this;
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--inline`]: inline,
                    [`${baseClass}--line-numbers`]: lineNumbers,
                    [`${baseClass}--theme-${theme}`]: ["light", "dark"].includes(theme)
                }}
            >
                <pre class={`${baseClass}__container language-${language}`}>
                    {lineNumbers && numLines > 0 && this.renderLineNumbers()}
                    {/* @ts-expect-error Check for typing */}
                    <code class={`language-${language}`} ref={(el) => (this.codeElement = el)} />
                </pre>
                {!inline && (
                    <div class={`${baseClass}__copy`}>
                        <button class={`${baseClass}__copy-btn`} type="button" onClick={copySource}>
                            {copyButton}
                            {!isCopied && <ux-icon name="copy" weight="normal"></ux-icon>}
                            {isCopied && <ux-icon name="checkmark" weight="normal"></ux-icon>}
                        </button>
                    </div>
                )}
            </Host>
        );
    }
}
